import {
  defineComponent,
  computed,
  ref,
  provide,
  inject,
  getCurrentInstance,
  watch,
  onMounted,
  onBeforeUnmount,
  withDirectives,
  Fragment,
  vShow,
  h,
  reactive,
} from 'vue'
import { useTimeoutFn } from '@vueuse/core'
import HlCollapseTransition from '@hongluan-ui/components/collapse-transition'
import HlTooltip from '@hongluan-ui/components/tooltip'
import { buildProps, definePropType, throwError } from '@hongluan-ui/utils'
import { SystemArrowRight } from '@hongluan-ui/components/system-icon'
import HlIcon from '@hongluan-ui/components/icon'
import { useNamespace, useDeprecated } from '@hongluan-ui/hooks'
import useMenu from './use-menu'

import type { Placement } from '@hongluan-ui/components/popper'
import type { ExtractPropTypes, VNodeArrayChildren, CSSProperties } from 'vue'
import type { MenuProvider, SubMenuProvider } from './types'

export const subMenuProps = buildProps({
  /**
   * @description unique identification
   */
  index: {
    type: String,
    required: true,
  },
  /**
   * @description timeout before showing a sub-menu(inherit `show-timeout` of the menu by default.)
   */
  showTimeout: Number,
  /**
   * @description timeout before hiding a sub-menu(inherit `hide-timeout` of the menu by default.)
   */
  hideTimeout: Number,
  /**
   * @description custom class name for the popup menu
   */
  fallbackPlacements: {
    type: definePropType<Placement[]>(Array),
  },
  /**
   * @description whether the sub-menu is disabled
   */
  popperClass: String,
  /**
   * @description whether the sub-menu is disabled
   */
  disabled: Boolean,
  /**
   * @description whether popup menu is teleported to the body
   */
  teleported: {
    type: Boolean,
    default: undefined,
  },
  popperAppendToBody: {
    type: Boolean,
    default: undefined,
  },
  /**
   * @description offset of the popper (overrides the `popper` of menu)
   */
  popperOffset: Number,
} as const)
export type SubMenuProps = ExtractPropTypes<typeof subMenuProps>

const COMPONENT_NAME = 'SubMenu'
export default defineComponent({
  name: COMPONENT_NAME,
  props: subMenuProps,

  setup(props, { slots, expose }) {
    useDeprecated(
      {
        from: 'popper-append-to-body',
        replacement: 'teleported',
        scope: COMPONENT_NAME,
        version: '2.5.0',
        ref: 'https://element-plus.org/en-US/component/menu.html#submenu-attributes',
      },
      computed(() => props.popperAppendToBody !== undefined),
    )

    const instance = getCurrentInstance()!
    const { namespace } = useNamespace()
    const { indexPath, parentMenu } = useMenu(
      instance,
      computed(() => props.index),
    )

    // inject
    const rootMenu = inject<MenuProvider>('rootMenu')
    if (!rootMenu) throwError(COMPONENT_NAME, 'can not inject root menu')

    const subMenu = inject<SubMenuProvider>(`subMenu:${parentMenu.value?.uid}`)
    if (!subMenu) throwError(COMPONENT_NAME, 'can not inject sub menu')

    const items = ref<MenuProvider['items']>({})
    const subMenus = ref<MenuProvider['subMenus']>({})

    let timeout: (() => void) | undefined
    const mouseInChild = ref(false)
    const verticalTitleRef = ref<HTMLDivElement>()
    const vPopper = ref<InstanceType<typeof HlTooltip> | null>(null)

    // computed
    const currentPlacement = computed<Placement>(() =>
      mode.value === 'horizontal' && isFirstLevel.value
        ? 'bottom-start'
        : 'right-start',
    )
    // const subMenuTitleIcon = computed(() => {
    //   return (mode.value === 'horizontal' && isFirstLevel.value) ||
    //     (mode.value === 'vertical' && !rootMenu.finalCollapse)
    //     ? SystemArrowDown
    //     : SystemArrowRight
    // })
    const isFirstLevel = computed(() => {
      let isFirstLevel = true
      let parent = instance.parent
      while (parent && parent.type.name !== 'Menu') {
        if (['SubMenu', 'MenuItemGroup'].includes(parent.type.name!)) {
          isFirstLevel = false
          break
        } else {
          parent = parent.parent
        }
      }
      return isFirstLevel
    })
    const appendToBody = computed(() => {
      const value = props.teleported ?? props.popperAppendToBody
      return value === undefined ? isFirstLevel.value : value
    })
    const menuTransitionName = computed(() =>
      rootMenu.finalCollapse ? 'zoom-in-left' : 'zoom-in-top',
    )
    const fallbackPlacements = computed<Placement[]>(() =>
      mode.value === 'horizontal' && isFirstLevel.value
        ? [
          'bottom-start',
          'bottom-end',
          'top-start',
          'top-end',
          'right-start',
          'left-start',
        ]
        : [
          'right-start',
          'right',
          'right-end',
          'left-start',
          'bottom-start',
          'bottom-end',
          'top-start',
          'top-end',
        ],
    )
    const opened = computed(() => rootMenu.openedMenus.includes(props.index))
    const active = computed(() => {
      let isActive = false

      Object.values(items.value).forEach(item => {
        if (item.active) {
          isActive = true
        }
      })

      Object.values(subMenus.value).forEach(subItem => {
        if (subItem.active) {
          isActive = true
        }
      })

      return isActive
    })

    const backgroundColor = computed(() => rootMenu.props.backgroundColor || '')
    const activeTextColor = computed(() => rootMenu.props.activeTextColor || '')
    const textColor = computed(() => rootMenu.props.textColor || '')
    const mode = computed(() => rootMenu.props.mode)
    const item = reactive({
      index: props.index,
      indexPath,
      active,
    })

    const titleStyle = computed<CSSProperties>(() => {
      if (mode.value !== 'horizontal') {
        return {
          color: textColor.value,
        }
      }
      return {
        borderBottomColor: active.value
          ? rootMenu.props.activeTextColor
            ? activeTextColor.value
            : ''
          : 'transparent',
        color: active.value ? activeTextColor.value : textColor.value,
      }
    })
    const subMenuPopperOffset = computed(() => {
      return props.popperOffset ?? rootMenu.props.popperOffset
    })
    const subMenuPopperClass = computed(() => {
      return props.popperClass ?? rootMenu.props.popperClass
    })

    const subMenuShowTimeout = computed(() => {
      return props.showTimeout ?? rootMenu.props.showTimeout
    })

    const subMenuHideTimeout = computed(() => {
      return props.hideTimeout ?? rootMenu.props.hideTimeout
    })

    // methods
    const doDestroy = () =>
      vPopper.value?.popperRef?.popperInstanceRef?.destroy()

    const handleCollapseToggle = (value: boolean) => {
      if (!value) {
        doDestroy()
      }
    }

    const handleClick = () => {
      if (
        (rootMenu.props.menuTrigger === 'hover' &&
          rootMenu.props.mode === 'horizontal') ||
        (rootMenu.finalCollapse && rootMenu.props.mode === 'vertical') ||
        props.disabled
      )
        return

      rootMenu.handleSubMenuClick({
        index: props.index,
        indexPath: indexPath.value,
        active: active.value,
      })
    }

    const handleMouseenter = (
      event: MouseEvent | FocusEvent,
      showTimeout = subMenuShowTimeout.value,
    ) => {
      if (event.type === 'focus') {
        return
      }
      if (
        (rootMenu.props.menuTrigger === 'click' &&
          rootMenu.props.mode === 'horizontal') ||
        (!rootMenu.finalCollapse && rootMenu.props.mode === 'vertical') ||
        props.disabled
      ) {
        subMenu.mouseInChild.value = true
        return
      }
      subMenu.mouseInChild.value = true

      timeout?.()
      ;({ stop: timeout } = useTimeoutFn(() => {
        rootMenu.openMenu(props.index, indexPath.value)
      }, showTimeout))

      if (appendToBody.value) {
        parentMenu.value!.vnode.el?.dispatchEvent(new MouseEvent('mouseenter'))
      }
    }

    const handleMouseleave = (deepDispatch = false) => {
      if (
        (rootMenu.props.menuTrigger === 'click' &&
          rootMenu.props.mode === 'horizontal') ||
        (!rootMenu.finalCollapse && rootMenu.props.mode === 'vertical')
      ) {
        subMenu.mouseInChild.value = false
        return
      }
      timeout?.()
      subMenu.mouseInChild.value = false
      ;({ stop: timeout } = useTimeoutFn(
        () =>
          !mouseInChild.value &&
          rootMenu.closeMenu(props.index, indexPath.value),
        subMenuHideTimeout.value,
      ))

      if (appendToBody.value && deepDispatch) {
        subMenu.handleMouseleave?.(true)
      }
    }

    watch(
      () => rootMenu.finalCollapse,
      value => handleCollapseToggle(Boolean(value)),
    )

    // provide
    {
      const addSubMenu: SubMenuProvider['addSubMenu'] = item => {
        subMenus.value[item.index] = item
      }
      const removeSubMenu: SubMenuProvider['removeSubMenu'] = item => {
        delete subMenus.value[item.index]
      }
      provide<SubMenuProvider>(`subMenu:${instance.uid}`, {
        addSubMenu,
        removeSubMenu,
        handleMouseleave,
        mouseInChild,
      })
    }

    // expose
    expose({
      opened,
    })

    // lifecycle
    onMounted(() => {
      rootMenu.addSubMenu(item)
      subMenu.addSubMenu(item)
    })

    onBeforeUnmount(() => {
      subMenu.removeSubMenu(item)
      rootMenu.removeSubMenu(item)
    })

    return () => {
      const titleTag: VNodeArrayChildren = [
        slots.title?.(),
        slots.icon ? slots.icon?.({ opened: opened.value, collapse: rootMenu.finalCollapse }) : h(
          HlIcon,
          {
            class: ['menu-arrow'],
          },
          { default: () => h(SystemArrowRight) },
        ),
      ]

      // this render function is only used for bypass `Vue`'s compiler caused patching issue.
      // temporarily mark ElPopper as any due to type inconsistency.
      const child = rootMenu.isMenuPopup
        ? h(
          // TODO: correct popper's type.
          HlTooltip as any,
          {
            ref: vPopper,
            manualMode: true,
            visible: opened.value,
            offset: subMenuPopperOffset.value,
            showArrow: false,
            persistent: true,
            popperClass: `${subMenuPopperClass.value ?? ''} ${namespace.value}-menu-popper`,
            placement: currentPlacement.value,
            teleported: appendToBody.value,
            fallbackPlacements: fallbackPlacements.value,
            transition: menuTransitionName.value,
            gpuAcceleration: false,
          },
          {
            content: () =>
              h(
                'div',
                {
                  class: [`menu-${mode.value}`, subMenuPopperClass.value],
                  onMouseenter: (evt: MouseEvent) =>
                    handleMouseenter(evt, 100),
                  onMouseleave: () => handleMouseleave(true),
                  onFocus: (evt: FocusEvent) => handleMouseenter(evt, 100),
                },
                [
                  h(
                    'ul',
                    {
                      class: [
                        namespace.value + '-menu menu-dropdown',
                        `popup-${currentPlacement.value}`,
                      ],
                    },
                    [slots.default?.()],
                  ),
                ],
              ),
            default: () =>
              h(
                'div',
                {
                  class: 'item-content',
                  style: [
                    titleStyle.value,
                    { backgroundColor: backgroundColor.value },
                  ],
                  onClick: handleClick,
                },
                titleTag,
              ),
          },
        )
        : h(Fragment, {}, [
          h(
            'div',
            {
              class: 'item-content',
              style: [
                titleStyle.value,
                { backgroundColor: backgroundColor.value },
              ],
              ref: verticalTitleRef,
              onClick: handleClick,
            },
            titleTag,
          ),
          h(
            HlCollapseTransition,
            {},
            {
              default: () =>
                withDirectives(
                  h(
                    'ul',
                    {
                      role: 'menu',
                      class: namespace.value + '-menu menu-inline',
                    },
                    [slots.default?.()],
                  ),
                  [[vShow, opened.value]],
                ),
            },
          ),
        ])
      return h(
        'li',
        {
          class: [
            `${namespace.value}-menu-item ${namespace.value}-sub-menu`,
            {
              'is-active': active.value,
              'is-opened': opened.value,
              'is-disabled': props.disabled,
            },
          ],
          role: 'menuitem',
          ariaHaspopup: true,
          ariaExpanded: opened.value,
          onMouseenter: handleMouseenter,
          onMouseleave: () => handleMouseleave(),
          onFocus: handleMouseenter,
        },
        [child],
      )
    }
  },
})
